'use client';

import { useState, useEffect, useRef } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import Icon from '@/components/ui/AppIcon';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

const SUGGESTED_QUERIES = [
  'Pourquoi Toronto est favori ce soir ?',
  'Quels matchs présentent les plus fortes anomalies statistiques ?',
  'Quels gardiens performent au-dessus des attentes ?',
  'Montre les équipes les plus sous-évaluées récemment.',
  'Analyse le momentum des Bruins cette semaine.',
  'Quelles lignes de paris montrent des mouvements suspects ?',
];

const SYSTEM_PROMPT = `You are Analytics Copilot, an elite sports intelligence assistant for NHL Analytics Pro — a hedge fund-grade sports analytics platform.

Your persona:
- Tone: institutional, analytical, precise — like a quant analyst at a sports hedge fund
- Style: concise, data-driven, no fluff
- Language: respond in the same language as the user (French or English)
- Focus: NHL data, betting markets, player performance, team analytics, predictive modeling

Your capabilities:
- Natural language queries about NHL games, teams, players, standings
- Statistical anomaly detection and market inefficiency analysis
- Goalie performance vs expected save percentage
- Team momentum and fatigue analysis
- Line movement interpretation and sharp money signals
- Contextual analysis with session memory

When answering:
1. Lead with the key insight (1-2 sentences max)
2. Support with 2-3 specific data points or metrics
3. End with a confidence level or actionable signal
4. Use structured formatting when presenting multiple data points
5. Always maintain analytical objectivity

Current context: NHL 2025-26 season, real-time data available.`;

export default function AnalyticsCopilotClient() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionHistory, setSessionHistory] = useState<Array<{ role: string; content: string }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.isStreaming) {
          const updated = [...prev];
          updated[updated.length - 1] = { ...last, content: response, isStreaming: false };
          return updated;
        }
        return prev;
      });
      setSessionHistory(prev => [...prev, { role: 'assistant', content: response }]);
    }
  }, [response, isLoading]);

  useEffect(() => {
    if (isLoading && response) {
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.isStreaming) {
          const updated = [...prev];
          updated[updated.length - 1] = { ...last, content: response };
          return updated;
        }
        return prev;
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (text?: string) => {
    const query = text || input.trim();
    if (!query || isLoading) return;

    const userMsg: Message = { role: 'user', content: query, timestamp: new Date() };
    const assistantMsg: Message = { role: 'assistant', content: '', timestamp: new Date(), isStreaming: true };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');

    const newHistory = [...sessionHistory, { role: 'user', content: query }];
    setSessionHistory(newHistory);

    const apiMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...newHistory,
    ];

    sendMessage(apiMessages, { max_completion_tokens: 1024 });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearSession = () => {
    setMessages([]);
    setSessionHistory([]);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center">
            <Icon name="SparklesIcon" size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Analytics Copilot</h1>
            <p className="text-xs text-muted-foreground font-mono-data">GPT-4 · NHL Intelligence Engine · Session Active</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-positive-subtle border border-positive/20">
            <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
            <span className="text-2xs font-mono-data text-positive">LIVE</span>
          </div>
          {messages.length > 0 && (
            <button onClick={clearSession} className="btn-ghost text-xs text-muted-foreground hover:text-foreground">
              <Icon name="ArrowPathIcon" size={14} className="mr-1" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-1">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                <Icon name="SparklesIcon" size={32} className="text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-1">Analytics Copilot</h2>
              <p className="text-sm text-muted-foreground max-w-md">
                Interrogez la plateforme en langage naturel. Analyses institutionnelles, anomalies statistiques, intelligence marché.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-2xl">
              {SUGGESTED_QUERIES.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSend(q)}
                  className="text-left px-4 py-3 rounded-xl bg-card border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-sm text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ChatBubbleLeftRightIcon" size={14} className="inline mr-2 text-primary/60" />
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="SparklesIcon" size={14} className="text-primary" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.role === 'user' ?'bg-primary text-primary-foreground rounded-tr-sm' :'bg-card border border-border rounded-tl-sm'
              }`}>
                {msg.role === 'assistant' && msg.isStreaming && !msg.content && (
                  <div className="flex gap-1 py-1">
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                {msg.role === 'assistant' && msg.isStreaming && msg.content && (
                  <span className="inline-block w-1 h-4 bg-primary/60 animate-pulse ml-0.5 align-middle" />
                )}
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-lg bg-muted border border-border flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name="UserCircleIcon" size={16} className="text-muted-foreground" />
                </div>
              )}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-card border border-border rounded-2xl p-3 flex gap-3 items-end">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Interrogez la plateforme... (ex: Quels matchs présentent des anomalies ?)"
          rows={1}
          disabled={isLoading}
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none outline-none min-h-[36px] max-h-[120px] py-1.5"
          style={{ height: 'auto' }}
          onInput={e => {
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = Math.min(t.scrollHeight, 120) + 'px';
          }}
        />
        <button
          onClick={() => handleSend()}
          disabled={!input.trim() || isLoading}
          className="w-9 h-9 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all flex-shrink-0"
        >
          {isLoading ? (
            <Icon name="ArrowPathIcon" size={16} className="text-primary-foreground animate-spin" />
          ) : (
            <Icon name="PaperAirplaneIcon" size={16} className="text-primary-foreground" />
          )}
        </button>
      </div>
      <p className="text-center text-2xs text-muted-foreground mt-2 font-mono-data">
        ANALYTICS COPILOT · GPT-4 · HEDGE FUND ANALYTICS TONE · SESSION MEMORY ACTIVE
      </p>
    </div>
  );
}
